import { useState } from "react";
import { SIGNING_CONFIGURED, signOutNow, useSession } from "../../auth/useAuth.ts";
import { shortAddr } from "../../lib/format.ts";
import { SIGNED_OUT_COPY } from "../../stores/session.ts";
import { Button } from "../../ui/Button.tsx";
import { SignInDialog } from "./SignInDialog.tsx";
import s from "./auth.module.css";

/**
 * THE TOP BAR'S SESSION CONTROL — and it renders SESSION EXPIRY AS A STATE, never as an error.
 *
 * Four states, four different sentences, because they have four different causes and four different fixes.
 * Collapsing them into "please sign in" is what produces "it logged me out for no reason" — the user has no
 * way to learn that a backgrounded tab idles out at 60 minutes unless something says so.
 *
 * IT IS ABSENT, NOT DISABLED, when the server says `enabled:false`: a dead control is indistinguishable from
 * a broken one. But an UNKNOWN config (a failed read, a restarting API) still renders it, because everything
 * in this control except sign-in is local state — and hiding it on a failed config read is exactly how a
 * whole wallet menu once vanished during a restart and stayed gone until a manual reload.
 */
export function SessionChip() {
  const session = useSession();
  const [dialog, setDialog] = useState(false);
  const [menu, setMenu] = useState(false);

  // The control renders even when signing is unconfigured: the DIALOG explains why, which is strictly more
  // useful than a bar with nothing in it. What it must never be is a button that looks live and is not.

  if (session.status === "signedIn") {
    return (
      <div className={s.chipWrap}>
        <Button
          className={s.chip}
          onClick={() => setMenu((v) => !v)}
          title={session.address ?? undefined}
          aria-expanded={menu}
        >
          <span className={session.signer === true ? s.dotOk : s.dotWarn} />
          {shortAddr(session.address ?? "")}
        </Button>
        {menu ? (
          <div className={s.menu}>
            <div className={s.mono}>{session.address}</div>
            {/* "SIGNED IN, CANNOT TRADE" IS A REAL STATE WITH ITS OWN COPY. A live bearer and a dead signing
                session are two clocks, and the Buy button is gated on both — so this line is the difference
                between a user who knows what to do and one who presses Buy and gets a vendor error. */}
            {session.signer === false ? (
              <p className={s.warn}>
                Your signing session has ended on this device. You can browse and see balances;
                signing needs a new sign-in.
              </p>
            ) : null}
            {session.signer === null ? (
              <p className={s.muted}>Checking your signing session…</p>
            ) : null}
            {/* SHOWN, NOT DISCOVERED. The session is ABSOLUTE — there is no sliding refresh — so a user who
                is told when it ends is a user who is not surprised mid-trade. */}
            {session.expiresAt !== null ? (
              <p className={s.muted}>
                This session ends at {new Date(session.expiresAt).toLocaleTimeString()} and does not
                extend itself. Renewing it costs one signature.
              </p>
            ) : null}
            {!SIGNING_CONFIGURED ? (
              <p className={s.warn}>Signing is not configured in this build.</p>
            ) : null}
            <Button onClick={() => void signOutNow().then(() => setMenu(false))}>
              Sign out on this device
            </Button>
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className={s.chipWrap}>
      <Button variant="primary" className={s.chip} onClick={() => setDialog(true)}>
        {session.status === "unknown" ? "Sign in" : signedOutLabel(session.reason)}
      </Button>
      {/* The REASON, beside the button, so the state is legible without opening anything. */}
      {session.status === "signedOut" && session.reason !== "never" ? (
        <span className={s.reason}>{SIGNED_OUT_COPY[session.reason]}</span>
      ) : null}
      <SignInDialog open={dialog} onClose={() => setDialog(false)} />
    </div>
  );
}

function signedOutLabel(reason: keyof typeof SIGNED_OUT_COPY): string {
  return reason === "idle" || reason === "expired" ? "Sign in again" : "Sign in";
}
